import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faSpinner,
  faInfoCircle,
  faDownload,
  faExpand,
  faFilter,
  faCalendarAlt,
  faClock,
  faFileInvoice,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { format, subDays, differenceInDays } from "date-fns";
import { calculateBusinessHours, KELLANOVA_THRESHOLDS, getPerformanceCategory } from "../utils/businessHours";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

const EnhancedAttentionDocumentTimesSeriesChart = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // Chart data state
  const [chartData, setChartData] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState({
    avgAttentionTime: 0,
    avgDocTime: 0,
    dataPoints: 0,
    dateRange: { start: null, end: null },
    trend: { attention: "neutral", doc: "neutral" },
  });

  // Time range state
  const [timeRange, setTimeRange] = useState("all"); // all, month, week

  // Expanded view state
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      processChartData(data, timeRange);
    }
  }, [data, isLoading, timeRange]);

  // Process chart data with time range filtering
  const processChartData = (data, range) => {
    // Filter valid data entries
    const validData = data.filter((item) => {
      const datesData = item?.header?.detail?.dates_data;
      const dateInit = new Date(datesData?.date_init);
      return !isNaN(dateInit.getTime()); // Ensure date_init is valid
    });

    if (validData.length === 0) {
      setChartData(null);
      return;
    }

    // Apply time range filter
    let filteredData = validData;
    if (range !== "all") {
      const now = new Date();
      const cutoffDate = new Date();

      if (range === "month") {
        cutoffDate.setMonth(now.getMonth() - 1);
      } else if (range === "week") {
        cutoffDate.setDate(now.getDate() - 7);
      }

      filteredData = validData.filter((item) => {
        const dateInit = new Date(item?.header?.detail?.dates_data?.date_init);
        return dateInit >= cutoffDate;
      });
    }

    // Group data by date with adaptive bucketing based on date range
    const earliestDate = new Date(
      Math.min(
        ...filteredData.map((item) =>
          new Date(item?.header?.detail?.dates_data?.date_init).getTime()
        )
      )
    );
    const latestDate = new Date(
      Math.max(
        ...filteredData.map((item) =>
          new Date(item?.header?.detail?.dates_data?.date_init).getTime()
        )
      )
    );

    // Determine appropriate bucketing
    const dateRangeDays = differenceInDays(latestDate, earliestDate);
    let bucketFormat = "yyyy-MM-dd"; // Daily bucketing by default

    if (dateRangeDays > 90) {
      bucketFormat = "yyyy-MM"; // Monthly bucketing for large ranges
    } else if (dateRangeDays > 30) {
      bucketFormat = "yyyy-'W'ww"; // Weekly bucketing for medium ranges
    }

    const groupedData = filteredData.reduce((acc, item) => {
      const datesData = item?.header?.detail?.dates_data;
      const dateInit = new Date(datesData?.date_init);
      const bucketKey = format(dateInit, bucketFormat);

      const attentionTime = calculateAttentionTimeValue(datesData);
      const docTime = calculateDocTimeValue(datesData);

      if (!acc[bucketKey]) {
        acc[bucketKey] = {
          attentionTimes: [],
          docTimes: [],
          count: 0,
          date: dateInit, // Store the actual date for proper display
        };
      }

      if (attentionTime !== null) {
        acc[bucketKey].attentionTimes.push(attentionTime);
      }

      if (docTime !== null) {
        acc[bucketKey].docTimes.push(docTime);
      }

      acc[bucketKey].count++;
      return acc;
    }, {});

    // Get sorted bucket keys
    const bucketKeys = Object.keys(groupedData).sort();

    if (bucketKeys.length === 0) {
      setChartData(null);
      return;
    }

    // Prepare data arrays with proper dates
    const sortedDates = bucketKeys.map((key) => groupedData[key].date);

    // Calculate cumulative averages
    let cumulativeAttentionSum = 0;
    let cumulativeAttentionCount = 0;
    let cumulativeDocSum = 0;
    let cumulativeDocCount = 0;

    // Calculate daily and cumulative averages
    const avgAttentionTimes = [];
    const avgDocTimes = [];
    const dailyAvgAttention = [];
    const dailyAvgDoc = [];
    const volumeData = [];

    bucketKeys.forEach((key) => {
      const attentionTimes = groupedData[key].attentionTimes || [];
      const docTimes = groupedData[key].docTimes || [];

      // Daily averages (only if there's data)
      const dailyAttentionAvg =
        attentionTimes.length > 0
          ? attentionTimes.reduce((a, b) => a + b, 0) / attentionTimes.length
          : null;

      const dailyDocAvg =
        docTimes.length > 0
          ? docTimes.reduce((a, b) => a + b, 0) / docTimes.length
          : null;

      // Volume data
      volumeData.push(groupedData[key].count);

      // Cumulative values
      cumulativeAttentionSum += attentionTimes.reduce((a, b) => a + b, 0);
      cumulativeAttentionCount += attentionTimes.length;
      cumulativeDocSum += docTimes.reduce((a, b) => a + b, 0);
      cumulativeDocCount += docTimes.length;

      // Calculate cumulative averages
      const cumulativeAttentionAvg =
        cumulativeAttentionCount === 0
          ? null
          : cumulativeAttentionSum / cumulativeAttentionCount;

      const cumulativeDocAvg =
        cumulativeDocCount === 0 ? null : cumulativeDocSum / cumulativeDocCount;

      // Store values
      avgAttentionTimes.push(cumulativeAttentionAvg);
      avgDocTimes.push(cumulativeDocAvg);
      dailyAvgAttention.push(dailyAttentionAvg);
      dailyAvgDoc.push(dailyDocAvg);
    });

    // Calculate analytics data
    const overallAvgAttention =
      cumulativeAttentionCount > 0
        ? cumulativeAttentionSum / cumulativeAttentionCount
        : 0;

    const overallAvgDoc =
      cumulativeDocCount > 0 ? cumulativeDocSum / cumulativeDocCount : 0;

    // Determine trends by comparing first and last halves
    const determineTrend = (dataset) => {
      const nonNullValues = dataset.filter((val) => val !== null);
      if (nonNullValues.length < 2) return "neutral";

      const firstHalf = nonNullValues.slice(
        0,
        Math.floor(nonNullValues.length / 2)
      );
      const secondHalf = nonNullValues.slice(
        Math.floor(nonNullValues.length / 2)
      );

      if (firstHalf.length === 0 || secondHalf.length === 0) return "neutral";

      const firstHalfAvg =
        firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
      const secondHalfAvg =
        secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

      if (firstHalfAvg === 0) return "neutral";

      const percentChange =
        ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;

      if (percentChange < -5) return "improving"; // Lower times are better
      if (percentChange > 5) return "worsening";
      return "neutral";
    };

    // Set analytics state
    setAnalytics({
      avgAttentionTime: overallAvgAttention.toFixed(1),
      avgDocTime: overallAvgDoc.toFixed(1),
      dataPoints: cumulativeAttentionCount + cumulativeDocCount,
      dateRange: {
        start:
          bucketKeys.length > 0
            ? format(groupedData[bucketKeys[0]].date, "yyyy-MM-dd")
            : null,
        end:
          bucketKeys.length > 0
            ? format(
                groupedData[bucketKeys[bucketKeys.length - 1]].date,
                "yyyy-MM-dd"
              )
            : null,
      },
      trend: {
        attention: determineTrend(dailyAvgAttention),
        doc: determineTrend(dailyAvgDoc),
      },
    });

    // Set chart data with sorted dates
    setChartData({
      labels: sortedDates,
      datasets: [
        {
          label: "Cumulative Avg Attention Time",
          data: avgAttentionTimes,
          borderColor: "rgba(46, 204, 113, 0.8)",
          backgroundColor: "rgba(46, 204, 113, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.2,
          pointRadius: (ctx) =>
            determinePointRadius(ctx, avgAttentionTimes.length),
          pointHoverRadius: 5,
          pointBackgroundColor: "rgba(46, 204, 113, 1)",
          yAxisID: "y",
        },
        {
          label: "Cumulative Avg Document Time",
          data: avgDocTimes,
          borderColor: "rgba(231, 76, 60, 0.8)",
          backgroundColor: "rgba(231, 76, 60, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.2,
          pointRadius: (ctx) => determinePointRadius(ctx, avgDocTimes.length),
          pointHoverRadius: 5,
          pointBackgroundColor: "rgba(231, 76, 60, 1)",
          yAxisID: "y",
        },
        {
          label: "Daily Avg Attention Time",
          data: dailyAvgAttention,
          borderColor: "rgba(46, 204, 113, 0.5)",
          borderDash: [5, 5],
          borderWidth: 1.5,
          fill: false,
          tension: 0,
          pointRadius: 0, // Hide points by default for less cluttered view
          pointHoverRadius: 4,
          hidden: true,
          yAxisID: "y",
        },
        {
          label: "Daily Avg Document Time",
          data: dailyAvgDoc,
          borderColor: "rgba(231, 76, 60, 0.5)",
          borderDash: [5, 5],
          borderWidth: 1.5,
          fill: false,
          tension: 0,
          pointRadius: 0, // Hide points by default for less cluttered view
          pointHoverRadius: 4,
          hidden: true,
          yAxisID: "y",
        },
        {
          label: "Volume",
          data: volumeData,
          borderColor: "rgba(52, 152, 219, 0.6)",
          backgroundColor: "rgba(52, 152, 219, 0.2)",
          borderWidth: 1,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 3,
          yAxisID: "y1",
          hidden: false,
          type: "bar",
        },
      ],
    });
  };

  // Helper for dynamic point radius based on number of data points
  const determinePointRadius = (ctx, totalPoints) => {
    if (!ctx || !ctx.raw) return 0;

    // Reduce point size when many data points
    if (totalPoints > 50) return 0;
    if (totalPoints > 30) return 1;
    if (totalPoints > 20) return 2;
    return 3;
  };

  // Determine appropriate time unit based on time range
  const getTimeUnit = () => {
    if (timeRange === "week") return "day";
    if (timeRange === "month") return "day";

    // For "all", check the analytics date range
    if (analytics.dateRange.start && analytics.dateRange.end) {
      const start = new Date(analytics.dateRange.start);
      const end = new Date(analytics.dateRange.end);
      const days = differenceInDays(end, start);

      if (days > 180) return "quarter";
      if (days > 60) return "month";
      return "week";
    }

    return "month";
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: getTimeUnit(),
          displayFormats: {
            day: "MMM d",
            week: "MMM d",
            month: "MMM yyyy",
            quarter: "Q yyyy",
          },
          tooltipFormat: "MMM d, yyyy",
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: "Date",
          font: {
            size: 12,
            weight: "bold",
          },
          padding: { top: 10 },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12, // Limit number of ticks to avoid crowding
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Time (hours)",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        ticks: {
          precision: 0,
          callback: function (value) {
            return value + "h";
          },
          maxTicksLimit: 8, // Limit number of ticks
        },
        grid: {
          color: "rgba(200, 200, 200, 0.2)",
        },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        title: {
          display: true,
          text: "Volume",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          precision: 0,
          maxTicksLimit: 6, // Limit number of ticks
        },
      },
    },
    plugins: {
      decimation: {
        enabled: true,
        algorithm: "min-max",
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#333",
        bodyColor: "#333",
        bodyFont: {
          size: 12,
        },
        borderColor: "rgba(200, 200, 200, 0.9)",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        itemSort: (a, b) => {
          // Custom sort order: Volume first, then Attention Time, then Document Time
          const order = [
            "Volume",
            "Cumulative Avg Attention Time",
            "Cumulative Avg Document Time",
            "Daily Avg Attention Time",
            "Daily Avg Document Time",
          ];
          return (
            order.indexOf(a.dataset.label) - order.indexOf(b.dataset.label)
          );
        },
        callbacks: {
          title: (tooltipItems) => {
            return format(new Date(tooltipItems[0].label), "MMMM d, yyyy");
          },
          label: (context) => {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              if (label.includes("Volume")) {
                return `${label} ${context.parsed.y} references`;
              } else {
                return `${label} ${context.parsed.y.toFixed(1)} hours`;
              }
            }
            return label;
          },
        },
      },
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 15,
          boxWidth: 10,
          font: {
            size: 11,
          },
        },
        onClick: function (e, legendItem, legend) {
          // Custom toggle behavior to keep at least one line visible
          const index = legendItem.datasetIndex;
          const ci = legend.chart;

          // Get currently visible datasets (excluding volume)
          const visibleLineDatasets = ci.data.datasets.filter(
            (ds, i) => i < 4 && !ci.getDatasetMeta(i).hidden
          );

          // Only toggle off if we have at least one other line visible
          if (
            ci.getDatasetMeta(index).hidden ||
            visibleLineDatasets.length > 1
          ) {
            ci.setDatasetVisibility(index, !ci.isDatasetVisible(index));
            ci.update();
          }
        },
      },
      title: {
        display: false,
        text: "Attention & Document Processing Times",
      },
    },
    // Prevent collision of labels
    layout: {
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 10,
      },
    },
    onClick: (_, elements) => {
      if (elements.length > 0 && typeof onChartItemClick === "function") {
        const index = elements[0].index;
        const date = chartData.labels[index];
        onChartItemClick({
          type: "timeSeries",
          date: date,
          values: {
            attentionTime: chartData.datasets[0].data[index],
            docTime: chartData.datasets[1].data[index],
            volume: chartData.datasets[4].data[index],
          },
        });
      }
    },
  };

  // Helper for formatting dates in the UI
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Get trend icon and color
  const getTrendIndicator = (trend) => {
    if (trend === "improving") {
      return { icon: "↓", color: "text-green-600", text: "Improving" }; // Downward arrow for improvement (lower times)
    } else if (trend === "worsening") {
      return { icon: "↑", color: "text-red-600", text: "Worsening" }; // Upward arrow for worse (higher times)
    } else {
      return { icon: "→", color: "text-gray-600", text: "Stable" }; // Right arrow for neutral
    }
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-indigo-600 mr-2"
        />
        <span className="text-gray-600">Loading chart data...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || !chartData) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faChartLine}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No time series data available</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faChartLine}
            className="text-indigo-600 mr-2"
          />
          Attention & Document Times Trend
        </h2>

        {/* Time Range Controls */}
        <div className="flex space-x-2">
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timeRange === "week"
                ? "bg-indigo-100 text-indigo-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimeRange("week")}
          >
            Week
          </button>
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timeRange === "month"
                ? "bg-indigo-100 text-indigo-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimeRange("month")}
          >
            Month
          </button>
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timeRange === "all"
                ? "bg-indigo-100 text-indigo-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimeRange("all")}
          >
            All
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Avg Attention Time</div>
          <div className="flex items-center">
            <div className="text-md font-bold">
              {analytics.avgAttentionTime}h
            </div>
            <div
              className={`ml-1 ${
                getTrendIndicator(analytics.trend.attention).color
              }`}
            >
              {getTrendIndicator(analytics.trend.attention).icon}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Avg Document Time</div>
          <div className="flex items-center">
            <div className="text-md font-bold">{analytics.avgDocTime}h</div>
            <div
              className={`ml-1 ${getTrendIndicator(analytics.trend.doc).color}`}
            >
              {getTrendIndicator(analytics.trend.doc).icon}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Date Range</div>
          <div className="text-xs font-medium">
            {formatDate(analytics.dateRange.start)} -{" "}
            {formatDate(analytics.dateRange.end)}
          </div>
        </div>

        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Data Points</div>
          <div className="text-md font-bold">{analytics.dataPoints}</div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <Line data={chartData} options={options} />
      </div>

      {/* Chart Info Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          <span>
            Cumulative averages show overall trends while daily values show
            fluctuations
          </span>
        </div>
        <div className="flex space-x-1">
          {/* Action buttons */}
          <button
            title="Download chart"
            className="p-1 hover:text-indigo-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-indigo-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <button
            title="Filter data"
            className="p-1 hover:text-indigo-600 transition-colors"
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-5/6 h-5/6 max-w-7xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                Attention & Document Processing Times Analysis
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div
              className="p-4 overflow-y-auto"
              style={{ height: "calc(100% - 65px)" }}
            >
              {/* Expanded Analytics Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Attention Time Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center mb-2">
                    <div className="p-2 rounded-full bg-green-100 text-green-600 mr-3">
                      <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-600">
                        Average Attention Time
                      </h4>
                      <div className="flex items-center mt-1">
                        <span className="text-2xl font-bold text-gray-900">
                          {analytics.avgAttentionTime}h
                        </span>
                        <span
                          className={`ml-2 text-sm ${
                            getTrendIndicator(analytics.trend.attention).color
                          }`}
                        >
                          {getTrendIndicator(analytics.trend.attention).icon}
                          {getTrendIndicator(analytics.trend.attention).text}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Time from creation to crossing date
                  </p>
                </div>

                {/* Document Time Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center mb-2">
                    <div className="p-2 rounded-full bg-red-100 text-red-600 mr-3">
                      <FontAwesomeIcon icon={faFileInvoice} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-600">
                        Average Document Time
                      </h4>
                      <div className="flex items-center mt-1">
                        <span className="text-2xl font-bold text-gray-900">
                          {analytics.avgDocTime}h
                        </span>
                        <span
                          className={`ml-2 text-sm ${
                            getTrendIndicator(analytics.trend.doc).color
                          }`}
                        >
                          {getTrendIndicator(analytics.trend.doc).icon}
                          {getTrendIndicator(analytics.trend.doc).text}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Time from creation to document completion
                  </p>
                </div>

                {/* Date Range Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center mb-2">
                    <div className="p-2 rounded-full bg-indigo-100 text-indigo-600 mr-3">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-600">
                        Analysis Period
                      </h4>
                      <div className="mt-1">
                        <div className="text-sm font-bold text-gray-900">
                          {formatDate(analytics.dateRange.start)}
                        </div>
                        <div className="text-sm font-bold text-gray-900">
                          to {formatDate(analytics.dateRange.end)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {timeRange === "all"
                      ? "All available data"
                      : timeRange === "month"
                      ? "Last 30 days"
                      : "Last 7 days"}
                  </p>
                </div>

                {/* Data Points Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center mb-2">
                    <div className="p-2 rounded-full bg-blue-100 text-blue-600 mr-3">
                      <FontAwesomeIcon icon={faChartBar} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-600">
                        Sample Size
                      </h4>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-gray-900">
                          {analytics.dataPoints}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">
                          data points
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Total references with valid time data
                  </p>
                </div>
              </div>

              {/* Large Chart */}
              <div
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-6"
                style={{ height: "450px" }}
              >
                <Line
                  data={chartData}
                  options={{
                    ...options,
                    plugins: {
                      ...options.plugins,
                      legend: {
                        ...options.plugins.legend,
                        position: "top",
                        labels: {
                          ...options.plugins.legend.labels,
                          padding: 25,
                          font: {
                            size: 12,
                          },
                        },
                      },
                    },
                    scales: {
                      ...options.scales,
                      x: {
                        ...options.scales.x,
                        ticks: {
                          ...options.scales.x.ticks,
                          maxTicksLimit: 20,
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Chart Explanation */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-3">
                  About This Chart
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">
                      Data Series
                    </h5>
                    <ul className="space-y-3 text-sm text-gray-600">
                      <li className="flex items-start">
                        <div className="w-3 h-3 bg-green-500 opacity-80 rounded-full mt-1 mr-2"></div>
                        <div>
                          <strong>Cumulative Avg Attention Time:</strong>{" "}
                          Running average of time from creation to crossing.
                          Shows overall processing efficiency trend over time.
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="w-3 h-3 bg-red-500 opacity-80 rounded-full mt-1 mr-2"></div>
                        <div>
                          <strong>Cumulative Avg Document Time:</strong> Running
                          average of time from creation to document completion.
                          Indicates document processing efficiency trends.
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="w-3 h-3 bg-blue-200 opacity-80 rounded-sm mt-1 mr-2"></div>
                        <div>
                          <strong>Volume:</strong> Number of references
                          processed in each time period. Helps correlate
                          processing times with workload.
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">
                      Chart Controls
                    </h5>
                    <ul className="space-y-3 text-sm text-gray-600">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 mr-2">
                          <FontAwesomeIcon
                            icon={faChartLine}
                            className="text-xs"
                          />
                        </div>
                        <div>
                          <strong>Legend Items:</strong> Click on legend items
                          to show/hide data series. Toggle daily averages to see
                          fluctuations.
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 mr-2">
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            className="text-xs"
                          />
                        </div>
                        <div>
                          <strong>Time Range:</strong> Use the Week/Month/All
                          buttons to focus on different time periods for more
                          detailed analysis.
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 mr-2">
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className="text-xs"
                          />
                        </div>
                        <div>
                          <strong>Hover & Click:</strong> Hover over data points
                          for detailed values. Click on points to drill down
                          into specific dates.
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to calculate attention time in business hours (excluding weekends)
const calculateAttentionTimeValue = (datesData) => {
  const dateInit = new Date(datesData?.date_init);
  const dateCrossing = new Date(datesData?.date_crossing);

  if (isNaN(dateInit.getTime()) || isNaN(dateCrossing.getTime())) {
    return null; // Return null for invalid dates
  }
  
  // Calculate business hours excluding weekends
  return calculateBusinessHours(dateInit, dateCrossing);
};

// Helper function to calculate document time in business hours (excluding weekends)
const calculateDocTimeValue = (datesData) => {
  const dateInit = new Date(datesData?.date_init);
  const dateDoda = new Date(datesData?.date_doda);

  if (
    isNaN(dateInit.getTime()) ||
    isNaN(dateDoda.getTime()) ||
    dateDoda <= dateInit
  ) {
    return null; // Return null for invalid dates
  }
  
  // Calculate business hours excluding weekends
  return calculateBusinessHours(dateInit, dateDoda);
};

export default EnhancedAttentionDocumentTimesSeriesChart;
